import { useField, useFormApi, useMultistepState, useScopedState, utils } from 'informed';

import Slider, { sliderClasses } from '@mui/material/Slider';
import { styled } from '@mui/material/styles';

import { CommonProps } from '../../layout/LoanForm';
import BoxTextField from '../BoxTextField/BoxTextField';
import { Button } from '@mui/material';
import { FieldMap } from '../../model/FieldMap';
import { stepsValue } from '../../model/formState';

const SliderWrapper = styled('div', { name: 'SliderWrapper' })`
  display: flex;
  flex-direction: column;
`;

const CzerwonkaSlider = styled(Slider)(({ theme }) => ({
  margin: '32px 0',

  [`.${sliderClasses.track}, .${sliderClasses.rail}`]: {
    height: 8,
    border: 0,
    background: 'var(--A71414)',
    opacity: 1,
  },

  [`.${sliderClasses.thumb}`]: {
    [theme.breakpoints.up('sm')]: {
      width: '2rem',
      height: '2rem',
    },

    [`&:before`]: {
      background: '#fff',
      width: '.9rem',
      height: '.9rem',

      [theme.breakpoints.up('sm')]: {
        width: '3.2rem',
        height: '3.2rem',
      },
    },
  },
}));

const { formatter, parser } = utils.createIntlNumberFormatter('pl-PL', {
  style: 'currency',
  currency: 'PLN',
});

export interface AmountCombinedFieldType {
  min: number;
  max: number;
  title: string | number;
  name: string;
  type: 'money' | 'month';
  currentValueBgColor: 'var(--C11A16)' | 'var(--A71414)';
}

const AmountCombinedField = ({
  t,
  tabindex,
  title,
  min,
  max,
  type,
  name,
  currentValueBgColor,
}: CommonProps & AmountCombinedFieldType) => {
  const isMonth = type === 'month';
  let monthMin = t.common.month;
  let monthMax = t.common.month;
  let monthVal = t.common.month;
  const { current } = useMultistepState();
  const { fieldState, render, ref } = useField({
    name,
    formatter,
    parser,
  });

  const formApi = useFormApi();

  // this is needed to refresh the component, without it slider is not gonna work
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { value: _scopeValue } = useScopedState();

  const { value } = fieldState as {
    value: number;
    maskedValue: string;
  };

  const handleChange = (event: Event, newValue: number | number[]) => {
    const value = newValue as number;
    formApi.setValue(`basicInformation.beforeAgreements.[${name}]`, value);
    formApi.setValue(`personalInformation.[${name}]`, value);
    formApi.setValue(`addressInformation.[${name}]`, value);
    formApi.setValue(`financeInformation.[${name}]`, value);
    formApi.setValue(`employeeInformation.[${name}]`, value);
  };

  if (isMonth) {
    if (min > 2) {
      monthMin = `${t.common.month}e`;
    }
    if (min > 5) {
      monthMin = t.common.month2;
    }

    if (max > 2) {
      monthMax = `${t.common.month}e`;
    }
    if (max > 5) {
      monthMax = t.common.month2;
    }

    if (value > 1) {
      monthVal = `${t.common.month}e`;
    }
    if (value > 4) {
      monthVal = t.common.month2;
    }
  }

  return render(
    <SliderWrapper sx={type === 'money' ? { margin: '32px 0' } : {}}>
      <BoxTextField
        title={title}
        value={`${value} ${isMonth ? monthVal : t.common.currency}`}
        backgroundColor2={currentValueBgColor}
        isAboveSlider
      />
      <CzerwonkaSlider
        ref={ref}
        min={min}
        step={isMonth ? 1 : 100}
        max={max}
        value={Number(value)}
        onChange={handleChange}
        aria-label="amount slider"
        tabIndex={Number(tabindex)}
      />
      <BoxTextField
        fontSize="14px"
        title={`${min} ${isMonth ? monthMin : t.common.currency}`}
        value={`${max} ${isMonth ? monthMax : t.common.currency}`}
        backgroundColor1={currentValueBgColor}
        backgroundColor2={currentValueBgColor}
      />

      {isMonth && current === stepsValue.BASIC_INFORMATION && (
        <Button
          variant="text"
          type="button"
          onClick={() => {
            const inputElement = document.getElementById(FieldMap.EMAIL)!;
            inputElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            inputElement.focus();
          }}
          tabIndex={Number(tabindex) + 1}
          sx={() => ({
            fontSize: '1.6rem',
            fontWeight: 700,
            lineHeight: '28px',
            textTransform: 'none',
            borderRadius: '.3rem',
            backgroundColor: '#fff',
            color: 'var(--D91E18)',
            width: 'auto',
            height: '5.6rem',
            marginTop: '2rem',
            // [theme.breakpoints.up("sm")]: {
            //   width: "100%",
            //   fontSize: "2.6rem",
            //   // px: '2rem',
            //   py: "1.1rem",
            // },

            [`&:hover`]: {
              //   backgroundColor: "var(--D91E18)",
              backgroundColor: '#fff',
            },
          })}
        >
          {t.common.moveToForm}
        </Button>
      )}
    </SliderWrapper>,
  );
};

export default AmountCombinedField;
